import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { 
  Shield, ClipboardList, Phone, LayoutGrid, Mail, Network, Headphones, 
  Database, ChevronRight, Check
} from 'lucide-react';
import { API } from '../App';

const CLOUD_IMG = "https://images.pexels.com/photos/14760650/pexels-photo-14760650.jpeg";

const iconMap = {
  ClipboardList,
  Phone,
  LayoutGrid,
  Mail,
  Network,
  Shield,
  Headphones,
  Database,
};

const Navigation = () => {
  const navigate = useNavigate();

  return (
    <nav className="nav-glass fixed top-0 left-0 right-0 z-50" data-testid="services-navigation">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <div className="w-10 h-10 bg-[#00F0FF] flex items-center justify-center">
            <Shield className="w-6 h-6 text-black" />
          </div>
          <span className="font-bold text-xl tracking-tight">Digital Associates</span>
        </Link>
        
        <div className="hidden md:flex items-center gap-8">
          <Link to="/search" className="text-[#A1A1AA] hover:text-white transition-colors" data-testid="nav-search">
            AI Search
          </Link>
          <Link to="/services" className="text-white transition-colors" data-testid="nav-services">
            Services
          </Link>
          <Link to="/vdi" className="text-[#A1A1AA] hover:text-white transition-colors" data-testid="nav-vdi">
            Remote Access
          </Link>
          <Link to="/contact" className="text-[#A1A1AA] hover:text-white transition-colors" data-testid="nav-contact">
            Contact
          </Link>
        </div>

        <Link to="/contact">
          <Button className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 btn-glow font-medium">
            Get in Touch
          </Button>
        </Link>
      </div>
    </nav>
  );
};

const ServiceCard = ({ service, index }) => {
  const IconComponent = iconMap[service.icon] || Shield;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="service-card p-6"
      data-testid={`service-card-${service.service_id}`}
    >
      <div className="flex items-start gap-4 mb-4">
        <div className="w-12 h-12 bg-[#00F0FF]/10 flex items-center justify-center flex-shrink-0">
          <IconComponent className="w-6 h-6 text-[#00F0FF]" />
        </div>
        <div>
          <h3 className="text-xl font-bold mb-1">{service.name}</h3>
          <span className="text-xs uppercase tracking-wider text-[#A1A1AA] font-mono">
            {service.category}
          </span>
        </div>
      </div>
      
      <p className="text-[#A1A1AA] mb-6 leading-relaxed">
        {service.description}
      </p>
      
      <ul className="space-y-2">
        {service.features.map((feature, idx) => (
          <li key={idx} className="flex items-center gap-2 text-sm">
            <Check className="w-4 h-4 text-[#00FF94] flex-shrink-0" />
            <span className="text-[#A1A1AA]">{feature}</span>
          </li>
        ))}
      </ul>
    </motion.div>
  );
};

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const servicesRes = await fetch(`${API}/services/`);
        const servicesData = await servicesRes.json();
        setServices(servicesData);
      } catch (error) {
        console.error('Failed to fetch services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const categories = [...new Set(services.map(s => s.category))];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="services-page">
      <Navigation />

      {/* Hero */}
      <section 
        className="hero-bg pt-32 pb-20 px-6 relative"
        style={{ backgroundImage: `url(${CLOUD_IMG})` }}
      >
        <div className="relative z-10 max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Enterprise <span className="text-[#00F0FF]">IT Services</span>
            </h1>
            <p className="text-xl text-[#A1A1AA] leading-relaxed">
              Comprehensive IT solutions designed for modern businesses. 
              From project management to cybersecurity, we've got you covered.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-6" data-testid="services-grid">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="service-card p-6 h-64 skeleton" />
              ))}
            </div>
          ) : (
            <>
              {categories.map((category) => (
                <div key={category} className="mb-16">
                  <h2 className="text-2xl font-bold mb-6 capitalize flex items-center gap-3">
                    <div className="w-2 h-8 bg-[#00F0FF]" />
                    {category}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {services
                      .filter(s => s.category === category)
                      .map((service, index) => (
                        <ServiceCard key={service.service_id} service={service} index={index} />
                      ))
                    }
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-[#0A0A0A]" data-testid="services-cta">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-[#A1A1AA] text-lg mb-8">
              Contact us today to discuss how we can help transform your IT infrastructure.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                onClick={() => navigate('/contact')}
                size="lg"
                className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 btn-glow px-8"
                data-testid="services-contact-btn"
              >
                Get in Touch
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-[#27272A] hover:bg-[#151515] px-8"
                onClick={() => window.location.href = 'tel:02035820582'}
              >
                Call 0203 582 0582
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-[#27272A]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center">
          <div className="flex items-center gap-3 mb-4 md:mb-0">
            <div className="w-8 h-8 bg-[#00F0FF] flex items-center justify-center">
              <Shield className="w-4 h-4 text-black" />
            </div>
            <span className="font-bold">Digital Associates</span>
          </div>
          <p className="text-sm text-[#A1A1AA]">
            © 2025 Digital Associates. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
